var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
/* eslint-disable @typescript-eslint/no-explicit-any */
import { PostEffect, RenderTarget, Shader, Texture, shaderChunks, SEMANTIC_POSITION, PIXELFORMAT_R8_G8_B8_A8, FILTER_NEAREST, ADDRESS_CLAMP_TO_EDGE, } from "playcanvas";
/**
 * Progressive anti-aliasing for static scenes.
 *
 * Ported from the string-configurator project (commit c7ca9fc), with the
 * WebGL2 check adjusted for PlayCanvas 1.68 and an `onComplete` callback so
 * offline renders (the thumbnail generator) can wait for the result.
 *
 * Produces a supersampled result at the current canvas resolution by
 * rendering multiple frames with sub-pixel projection jitter (Halton 2,3
 * sequence) and incrementally blending them together. No canvas resize or
 * additional full-size framebuffer is needed, only two screen-sized
 * ping-pong render targets for the accumulation.
 *
 * Lifecycle is driven entirely by the PlayCanvas app's `autoRender` flag:
 *
 * - When `autoRender` transitions from `true` to `false` (i.e. continuous
 *   rendering stops, typically because camera motion or interaction ended),
 *   accumulation starts automatically.
 * - While accumulating, the class requests additional frames via
 *   `app.renderNextFrame`, applying a different sub-pixel jitter offset to
 *   the camera projection matrix on each one.
 * - Once all samples have been collected, a final clean (un-jittered) frame
 *   is rendered. The accumulated result then remains on screen for as long
 *   as `autoRender` stays `false`, no further frames are rendered.
 * - If `autoRender` turns back on (e.g. the user starts interacting again),
 *   accumulation is cancelled and the post-effect is removed so that normal
 *   rendering resumes without interference.
 *
 * This means consumers only need to construct the instance and later call
 * `destroy()` when it is no longer needed. Starting and stopping is handled
 * internally.
 */
var ProgressiveAA = /** @class */ (function () {
    function ProgressiveAA(app, cameraComponent, options) {
        var _this = this;
        var _a;
        this._currentSample = 0;
        this._active = false;
        this._hadAutoRender = false;
        this._onFrameRender = function () {
            var _a;
            var autoRender = _this._app.autoRender;
            var stop = autoRender && _this._active;
            var start = !autoRender && _this._hadAutoRender;
            if (stop) {
                _this._stop();
            }
            else if (start) {
                _this._start();
            }
            _this._hadAutoRender = autoRender;
            if (!_this._active) {
                return;
            }
            var sampleIndex = _this._currentSample;
            if (sampleIndex < _this._sampleCount) {
                // Incremental averaging: blend factor = i / (i + 1)
                // Sample 0: factor 0 → 100% current, Sample 7: factor 7/8 → 87.5% accum
                _this._effect.blendFactor = sampleIndex / (sampleIndex + 1);
                _this._applyJitter(_this._currentSample);
            }
            else if (sampleIndex === _this._sampleCount) {
                // All samples done, render one final clean frame without jitter,
                // showing 100% of the accumulated result
                _this._clearJitter();
                _this._effect.blendFactor = 1.0;
            }
            if (sampleIndex <= _this._sampleCount) {
                _this._currentSample++;
                _this._app.renderNextFrame = true;
            }
            else if (sampleIndex === _this._sampleCount + 1) {
                // The clean frame requested on the previous tick has been rendered by
                // now (the app renders after firing "framerender"), so the
                // accumulated image is what is on screen.
                _this._currentSample++;
                (_a = _this._onComplete) === null || _a === void 0 ? void 0 : _a.call(_this);
            }
        };
        this._onResize = function () {
            // When rendertargets are resized, we need to restart the sampling (if active)
            // since the rendertargets will have lost any accumulated data.
            _this._currentSample = 0;
        };
        this._app = app;
        this._cameraComponent = cameraComponent;
        this._sampleCount = (_a = options === null || options === void 0 ? void 0 : options.samples) !== null && _a !== void 0 ? _a : 8;
        this._onComplete = options === null || options === void 0 ? void 0 : options.onComplete;
        this._effect = new AccumulationEffect(app.graphicsDevice, this._onResize);
        // Always watch for renders so we can auto-start/restart accumulation
        this._app.on("framerender", this._onFrameRender);
    }
    Object.defineProperty(ProgressiveAA.prototype, "isActive", {
        get: function () {
            return this._active;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Release GPU resources.
     */
    ProgressiveAA.prototype.destroy = function () {
        this._stop();
        this._app.off("framerender", this._onFrameRender);
        this._effect.destroy();
    };
    ProgressiveAA.prototype._start = function () {
        if (this._active) {
            return;
        }
        this._active = true;
        this._currentSample = 0;
        this._cameraComponent.postEffects.addEffect(this._effect);
    };
    ProgressiveAA.prototype._stop = function () {
        if (!this._active) {
            return;
        }
        this._active = false;
        this._clearJitter();
        this._cameraComponent.postEffects.removeEffect(this._effect);
    };
    ProgressiveAA.prototype._applyJitter = function (sampleIndex) {
        var camera = this._cameraComponent.camera;
        camera._calculateProjection = this._createJitterCallback(sampleIndex);
    };
    ProgressiveAA.prototype._createJitterCallback = function (sampleIndex) {
        // Halton(2,3) sequence gives well-distributed sub-pixel offsets
        var jitterX = halton(sampleIndex + 1, 2) - 0.5;
        var jitterY = halton(sampleIndex + 1, 3) - 0.5;
        var device = this._app.graphicsDevice;
        var camera = this._cameraComponent.camera;
        var callback = function (projMat, _view) {
            // Temporarily clear the override so the default projection is computed
            camera._calculateProjection = null;
            camera._projMatDirty = true;
            // Force PlayCanvas to compute the standard projection matrix.
            // Since projMat IS pcCamera._projMat, reading the getter fills it
            // with the correct default perspective/ortho values.
            void camera.projectionMatrix; // eslint-disable-line @typescript-eslint/no-unused-expressions
            // Restore our override for subsequent calls in this frame
            camera._calculateProjection = callback;
            // Apply sub-pixel offset to the projection matrix.
            // Elements [8] and [9] shift the image in X and Y (NDC space).
            projMat.data[8] += (2.0 * jitterX) / device.width;
            projMat.data[9] += (2.0 * jitterY) / device.height;
        };
        return callback;
    };
    ProgressiveAA.prototype._clearJitter = function () {
        var camera = this._cameraComponent.camera;
        camera._calculateProjection = null;
        camera._projMatDirty = true;
    };
    return ProgressiveAA;
}());
export { ProgressiveAA };
/** Halton sequence for quasi-random sub-pixel jitter offsets, in [0, 1). */
export function halton(index, base) {
    var result = 0;
    var f = 1 / base;
    var i = index;
    while (i > 0) {
        result += f * (i % base);
        i = Math.floor(i / base);
        f /= base;
    }
    return result;
}
/**
 * Post-effect that blends the current rendered frame with a previous
 * accumulation texture, and stores the result for the next iteration.
 *
 * On each frame it does two draw calls:
 *   1. Blend input + previous accumulation → write to internal accumulation RT
 *   2. Copy the accumulation RT → output (screen or next effect)
 */
var AccumulationEffect = /** @class */ (function (_super) {
    __extends(AccumulationEffect, _super);
    function AccumulationEffect(device, onResize) {
        var _this = _super.call(this, device) || this;
        _this.blendFactor = 0.0;
        // Ping-pong pair: avoids read-write hazard (can't read and write the same RT)
        _this._rtA = null;
        _this._rtB = null;
        _this._writeToA = true;
        _this.needsDepthBuffer = false;
        _this._onResize = onResize;
        _this._createShaders();
        return _this;
    }
    AccumulationEffect.prototype.render = function (inputTarget, outputTarget, rect) {
        this._ensureRTs(inputTarget);
        var readRT = this._writeToA ? this._rtB : this._rtA;
        var writeRT = this._writeToA ? this._rtA : this._rtB;
        var scope = this.device.scope;
        scope.resolve("uColorBuffer").setValue(inputTarget.colorBuffer);
        scope.resolve("uAccumBuffer").setValue(readRT.colorBuffer);
        scope.resolve("uBlendFactor").setValue(this.blendFactor);
        this.drawQuad(writeRT, this._blendShader, rect);
        scope.resolve("uTexture").setValue(writeRT.colorBuffer);
        this.drawQuad(outputTarget, this._copyShader, rect);
        // Swap for next frame
        this._writeToA = !this._writeToA;
    };
    AccumulationEffect.prototype.destroy = function () {
        this._destroyRTs();
    };
    AccumulationEffect.prototype._ensureRTs = function (reference) {
        var _this = this;
        var refTex = reference.colorBuffer;
        if (this._rtA &&
            this._rtA.colorBuffer.width === refTex.width &&
            this._rtA.colorBuffer.height === refTex.height) {
            return;
        }
        if (this._rtA) {
            this._onResize();
        }
        this._destroyRTs();
        var createRT = function (name) {
            return new RenderTarget({
                colorBuffer: new Texture(_this.device, {
                    name: name,
                    width: refTex.width,
                    height: refTex.height,
                    format: PIXELFORMAT_R8_G8_B8_A8,
                    minFilter: FILTER_NEAREST,
                    magFilter: FILTER_NEAREST,
                    addressU: ADDRESS_CLAMP_TO_EDGE,
                    addressV: ADDRESS_CLAMP_TO_EDGE,
                    mipmaps: false,
                }),
                depth: false,
            });
        };
        this._rtA = createRT("PAA_PingA");
        this._rtB = createRT("PAA_PingB");
    };
    AccumulationEffect.prototype._destroyRTs = function () {
        var e_1, _a;
        try {
            for (var _b = __values([this._rtA, this._rtB]), _c = _b.next(); !_c.done; _c = _b.next()) {
                var rt = _c.value;
                if (rt) {
                    rt.colorBuffer.destroy();
                    rt.destroy();
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_1) throw e_1.error; }
        }
        this._rtA = null;
        this._rtB = null;
    };
    AccumulationEffect.prototype._createShaders = function () {
        var _a;
        var device = this.device;
        // PlayCanvas 1.68 exposes `isWebGL2`; older engines used `webgl2`.
        var webgl2 = (_a = device.isWebGL2) !== null && _a !== void 0 ? _a : device.webgl2;
        var chunks = shaderChunks;
        var preambleVs = webgl2 ? "#version 300 es\n\n" + chunks.gles3VS : "";
        var preambleFs = webgl2 ? "#version 300 es\n\n" + chunks.gles3PS : "";
        this._blendShader = new Shader(device, {
            attributes: { position: SEMANTIC_POSITION },
            vshader: preambleVs +
                "\n\t\t\t\tattribute vec2 position;\n\t\t\t\tvarying vec2 texcoord;\n\t\t\t\tvoid main() {\n\t\t\t\t\tgl_Position = vec4(position, 0.0, 1.0);\n\t\t\t\t\ttexcoord = (position + 1.0) * 0.5;\n\t\t\t\t}\n\t\t\t",
            fshader: preambleFs +
                "\n\t\t\t\tprecision highp float;\n\t\t\t\tuniform sampler2D uColorBuffer;\n\t\t\t\tuniform sampler2D uAccumBuffer;\n\t\t\t\tuniform float uBlendFactor;\n\t\t\t\tvarying vec2 texcoord;\n\n\t\t\t\tvoid main() {\n\t\t\t\t\tvec4 current = texture2D(uColorBuffer, texcoord);\n\t\t\t\t\tvec4 accum = texture2D(uAccumBuffer, texcoord);\n\t\t\t\t\t// blendFactor=0 on first sample \u2192 100% current frame\n\t\t\t\t\t// blendFactor=i/(i+1) on subsequent samples \u2192 incremental average\n\t\t\t\t\tgl_FragColor = mix(current, accum, uBlendFactor);\n\t\t\t\t}\n\t\t\t",
        });
        this._copyShader = new Shader(device, {
            attributes: { position: SEMANTIC_POSITION },
            vshader: preambleVs +
                "\n\t\t\t\tattribute vec2 position;\n\t\t\t\tvarying vec2 texcoord;\n\t\t\t\tvoid main() {\n\t\t\t\t\tgl_Position = vec4(position, 0.0, 1.0);\n\t\t\t\t\ttexcoord = (position + 1.0) * 0.5;\n\t\t\t\t}\n\t\t\t",
            fshader: preambleFs +
                "\n\t\t\t\tprecision highp float;\n\t\t\t\tuniform sampler2D uTexture;\n\t\t\t\tvarying vec2 texcoord;\n\n\t\t\t\tvoid main() {\n\t\t\t\t\tgl_FragColor = texture2D(uTexture, texcoord);\n\t\t\t\t}\n\t\t\t",
        });
    };
    return AccumulationEffect;
}(PostEffect));
